import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Brain,
  Code,
  GitGraph,
  Users,
  GraduationCap,
  Award,
  Building,
  Globe,
  Book,
  Zap,
  Lightbulb
} from "lucide-react";

const AboutUs = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* ---------- data ---------- */
  const metrics = [
    { number: "20+", label: "Countries reached" },
    { number: "20,000+", label: "Professionals trained" },
    { number: "95%", label: "Client satisfaction rate" },
    { number: "100+", label: "Enterprise implementations" }
  ];

  const services = [
    {
      icon: <Brain className="w-10 h-10 text-blue-600" />,
      title: "Enterprise AI Solutions",
      description:
        "Custom AI implementations, from generative AI to autonomous agents, tailored to your specific business challenges."
    },
    {
      icon: <GitGraph className="w-10 h-10 text-purple-600" />,
      title: "Model Context Protocol",
      description:
        "Advanced context management for enhanced AI security, performance, and seamless enterprise integration."
    },
    {
      icon: <Code className="w-10 h-10 text-indigo-600" />,
      title: "Custom LLM Development",
      description:
        "Fine-tuned language models optimized for your industry-specific requirements and use cases."
    },
    {
      icon: <GraduationCap className="w-10 h-10 text-green-600" />,
      title: "Corporate Training",
      description:
        "Comprehensive AI education programs for executives, managers, and technical teams."
    }
  ];

  const team = [
    {
      name: "Ritesh Vajariya",
      role: "Founder & AI Expert",
      image:
        "https://aigurufiles.s3.amazonaws.com/images/Ritesh-thumbnail.png",
      bio:
        "Silicon Valley veteran and global AI thought leader. Former Head of Generative AI Strategy at Cerebras Systems and AWS leader for SageMaker and Amazon Bedrock.",
      linkedin: "https://www.linkedin.com/in/vajariya"
    },
    {
      name: "Manan",
      role: "AI Education Evangelist",
      image: "https://aigurufiles.s3.amazonaws.com/images/manan-1.jpg",
      bio:
        "Passionate about making complex AI concepts accessible to learners of all backgrounds. Expert in curriculum development and educational delivery.",
      linkedin: "#"
    }
  ];

  /* ========== JSX ========== */
  return (
    <div className="bg-gray-50">
      {/* ---------- Hero ---------- */}
      <div className="relative w-full py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">
            About AI Guru
          </h1>
          <p className="text-xl sm:text-2xl max-w-3xl mx-auto leading-relaxed opacity-90">
            Transforming businesses and careers with enterprise AI solutions,
            expert implementation, and professional development.
          </p>
        </div>
      </div>

      {/* ---------- Our story + Core values (moved from Home) ---------- */}
      <section className="bg-gray-50 py-16 px-6">
        {/* Our story */}
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-6">
            <h2 className="text-4xl sm:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-lightblue to-salmon">
              Our story
            </h2>

            <p className="text-lg text-darkgray">
              AI Guru was founded by{" "}
              <span className="font-semibold text-lightblue">
                Ritesh Vajariya
              </span>
              , a Silicon Valley veteran and global AI thought leader. As Head
              of Generative AI Strategy at{" "}
              <span className="font-semibold">Cerebras Systems</span>, Ritesh
              drives innovation in AI infrastructure and large language models.
              Previously at <span className="font-semibold">AWS</span>, he led
              go-to-market strategies for{" "}
              <span className="font-semibold">Amazon SageMaker</span> and{" "}
              <span className="font-semibold">Amazon Bedrock</span>, boosting AI
              adoption across industries.
            </p>

            <p className="text-lg text-darkgray">
              With a passion for democratizing AI, he has delivered keynotes and
              workshops in <span className="font-semibold">20+ countries</span>,
              impacting{" "}
              <span className="font-semibold">15 000+ professionals</span>.
            </p>

            <p className="text-lg text-darkgray">
              AI Guru bridges cutting-edge AI solutions with accessible
              education, empowering professionals and organizations worldwide.
            </p>
          </div>

          {/* founder photos */}
          <div className="flex flex-col md:flex-row items-center gap-8">
            <img
              src="https://images.squarespace-cdn.com/content/v1/671f15a34acff55ddc02afbc/dec478a7-180a-4cb2-88e7-3cf12ccc2090/Ritesh-self-1.png?format=1500w"
              alt="Ritesh Vajariya"
              className="w-60 h-80 object-cover rounded-lg shadow-lg"
            />
            <img
              src="https://images.squarespace-cdn.com/content/v1/671f15a34acff55ddc02afbc/55bcce3b-7e94-4bf1-b0b5-174fac350e43/Ritesh-singapore-solo.png?format=1500w"
              alt="Ritesh Vajariya speaking"
              className="w-96 h-80 object-cover rounded-lg shadow-lg md:mt-24"
            />
          </div>
        </div>

        <div className="h-20" />

        {/* Core values */}
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* image */}
          <div className="flex justify-center items-center order-2 lg:order-1">
            <img
              src="https://images.squarespace-cdn.com/content/v1/671f15a34acff55ddc02afbc/70e25ddf-3dc2-43a0-a7cc-d77329a10479/mexico-event-1.jpeg?format=1500w"
              alt="AI Guru workshop"
              className="w-full object-cover rounded-lg shadow-lg"
            />
          </div>

          {/* text */}
          <div className="space-y-6 order-1 lg:order-2">
            <h2 className="text-4xl sm:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-lightblue to-salmon">
              Our core values
            </h2>

            <ul className="space-y-4 text-lg text-darkgray">
              <li>
                <span className="font-semibold">Accessibility:</span> Simplifying
                complex AI concepts into easy-to-understand modules.
              </li>
              <li>
                <span className="font-semibold">Innovation:</span> Continuously
                evolving our curriculum to keep pace with the AI landscape.
              </li>
              <li>
                <span className="font-semibold">Practical application:</span>{" "}
                Bridging theory and practice through real-world use cases.
              </li>
              <li>
                <span className="font-semibold">Community:</span> Building a
                supportive global network of learners and AI enthusiasts.
              </li>
              <li>
                <span className="font-semibold">Ethical AI:</span> Promoting
                responsible development and deployment of AI.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ---------- Our Mission ---------- */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* text */}
          <div className="md:order-1">
            <h2 className="text-4xl font-bold text-gray-800 mb-6">
              Our mission
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              AI Guru makes advanced AI accessible and impactful for
              organizations of all sizes through a dual approach:
            </p>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="mr-4 p-2 bg-blue-100 rounded-full">
                  <Building className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">
                    Enterprise solutions
                  </h3>
                  <p className="text-gray-600">
                    Delivering custom AI implementations that solve real
                    business challenges.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="mr-4 p-2 bg-blue-100 rounded-full">
                  <Book className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">
                    Knowledge transfer
                  </h3>
                  <p className="text-gray-600">
                    Empowering professionals with the skills to leverage AI
                    effectively.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* image */}
          <div className="flex items-center justify-center md:order-2">
            <img
              src="https://aigurufiles.s3.amazonaws.com/images/AboutImg.jpg"
              alt="AI Guru mission"
              className="rounded-lg shadow-xl max-w-full h-auto"
            />
          </div>
        </div>
      </div>

      {/* ---------- Services ---------- */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">
            What we offer
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
            Comprehensive AI solutions and education for every stage of your AI
            journey.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((s, i) => (
              <div
                key={i}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="mx-auto flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-6">
                  {s.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  {s.title}
                </h3>
                <p className="text-gray-600">{s.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <Link
              to="/solution"
              className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-3 px-8 rounded-lg hover:opacity-90 transition"
            >
              Explore our solutions
            </Link>
          </div>
        </div>
      </div>

      {/* ---------- Impact ---------- */}
      <div className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-16">Our impact by the numbers</h2>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {metrics.map((m, i) => (
              <div key={i} className="p-6">
                <div className="text-4xl md:text-5xl font-bold mb-2">
                  {m.number}
                </div>
                <p className="text-lg opacity-80">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ---------- Leadership ---------- */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-800 text-center mb-16">
            Our leadership
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {team.map((m, i) => (
              <div
                key={i}
                className="flex flex-col md:flex-row items-center md:items-start gap-8"
              >
                <img
                  src={m.image}
                  alt={m.name}
                  className="w-48 h-48 rounded-full object-cover shadow-lg"
                />
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    {m.name}
                  </h3>
                  <p className="text-blue-600 font-medium mb-4">{m.role}</p>
                  <p className="text-gray-600 mb-4">{m.bio}</p>
                  <a
                    href={m.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:text-blue-800"
                  >
                    <span className="mr-2">Connect on LinkedIn</span>
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ---------- Strategic partners ---------- */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-6">
            Our strategic partners
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
            We collaborate with leading organizations to deliver exceptional AI
            solutions and training.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {[
              "AWS",
              "Microsoft",
              "Google",
              "Coursera",
              "Udemy",
              "Maven"
            ].map((p, i) => (
              <div
                key={i}
                className="flex items-center justify-center p-4 grayscale hover:grayscale-0 transition-all"
              >
                <img
                  src={`https://aigurufiles.s3.amazonaws.com/images/${p.toLowerCase()}Logo.png`}
                  alt={p}
                  className="max-h-16"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ---------- Global reach ---------- */}
      <div className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl font-bold mb-6">Global reach</h2>
            <p className="text-xl mb-8">
              AI Guru has delivered AI solutions and training in over 20
              countries across 5 continents.
            </p>
            <div className="flex flex-wrap gap-3">
              {[
                "USA",
                "Singapore",
                "Mexico",
                "UK",
                "Canada",
                "India",
                "UAE",
                "Australia"
              ].map((c, i) => (
                <span
                  key={i}
                  className="px-3 py-1 bg-white/20 rounded-full text-sm"
                >
                  {c}
                </span>
              ))}
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
                +12 more
              </span>
            </div>
          </div>
          <div className="flex justify-center">
            <Globe className="w-64 h-64 text-white/80" />
          </div>
        </div>
      </div>

      {/* ---------- CTA ---------- */}
      <div className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-6">
            Ready to transform your business with AI?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Whether you're looking for enterprise AI solutions or professional
            development, AI Guru is your partner for success.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/solution"
              className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-3 px-8 rounded-lg hover:opacity-90 transition"
            >
              Explore solutions
            </Link>
            <Link
              to="/contactUs"
              className="inline-block bg-white text-gray-900 font-bold py-3 px-8 rounded-lg hover:bg-gray-100 transition"
            >
              Contact us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
